import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import TokenExpiredMessage from '../components/TokenExpiredMessage';
import { performLogin, TOKEN_TYPES, setToken } from '../utils/tokenManager';
import namkeAuthBanner from '../assets/namke-auth-banner.png';
import namkeLogoHeader from '../assets/namke-logo-header.png';
import API_BASE_URL from '../config/api';
import TurnstileWidget from '../components/TurnstileWidget';

const inputClass =
  'w-full min-h-[56px] px-6 bg-namke-cream/60 border border-namke-black/10 rounded-full focus:outline-none focus:ring-2 focus:ring-namke-darkGreen/40 focus:border-transparent transition-colors text-namke-black placeholder-namke-black/40 text-base';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const expired = urlParams.get('expired');
    const message = urlParams.get('message');
    if (expired === 'true') {
      setTokenExpired(true);
      setError(message || t('auth.sessionExpired'));
    }
  }, [location.search, location.state]);

  useEffect(() => {
    const msg = location.state?.message;
    if (msg) {
      setSuccessMessage(msg);
      navigate('/login', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await performLogin({ email, password, turnstile_token: turnstileToken }, TOKEN_TYPES.CLIENT);
      if (result.requires_2fa) {
        setTempToken(result.temp_token || '');
        const domain = email.split('@')[1] || '***';
        setMaskedEmail(`***@${domain}`);
        setShow2FA(true);
      } else if (result.success) {
        navigate('/');
      } else {
        setError(t('auth.invalidCredentials'));
      }
    } catch (err) {
      setError(err.message || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code: otpCode }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || t('auth.wrongCode'));
      }
      const data = await res.json();
      setToken(data.access_token, TOKEN_TYPES.CLIENT);
      navigate('/');
    } catch (err) {
      setError(err.message || t('auth.verificationError'));
    } finally {
      setLoading(false);
    }
  };

  if (tokenExpired) {
    return <TokenExpiredMessage message={error} />;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', background: '#FCFBF5' }}>
      {/* Colonne formulaire */}
      <div style={{ padding: 'clamp(24px, 4vw, 56px) clamp(20px, 5vw, 72px)', display: 'flex', flexDirection: 'column', gap: 'clamp(22px, 3vw, 36px)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
          <img src={namkeLogoHeader} alt="namke" style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
        </Link>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '26px', maxWidth: '440px', width: '100%', margin: '0 auto' }}>
          {!show2FA ? (
            <>
              <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23' }}>Connexion</span>
                <h1 style={{ margin: 0, fontWeight: 900, fontSize: 'clamp(32px, 3.8vw, 50px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Content de vous revoir</h1>
                <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.6, color: 'rgba(5,16,4,0.74)' }}>Connectez-vous pour retrouver vos marmites favorites.</p>
              </div>

              {/* Bascule client / pro — redirige vers l'espace restaurateur */}
              <div className="animate-fade-in-delay" style={{ display: 'flex', gap: '4px', background: '#FBF9D9', borderRadius: '999px', padding: '5px', alignSelf: 'flex-start' }}>
                <span style={{ background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', padding: '11px 24px', borderRadius: '999px' }}>
                  Je commande
                </span>
                <button
                  type="button"
                  onClick={() => navigate('/business/login')}
                  style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.66)', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', padding: '11px 24px', borderRadius: '999px' }}
                >
                  Je cuisine
                </button>
              </div>

              {successMessage && (
                <div className="form-message form-message--success text-center">{successMessage}</div>
              )}
              {error && (
                <div className="form-message form-message--error text-center">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="animate-fade-in-delay-2" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>E-mail</span>
                  <input
                    type="email"
                    className={inputClass}
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Mot de passe</span>
                  <span className="flex items-center gap-3" style={{ border: '1px solid rgba(5,16,4,0.1)', background: 'rgba(251,249,217,0.6)', borderRadius: '999px', padding: '0 6px 0 24px', minHeight: '56px' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '16px', color: '#051004', outline: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ border: 'none', cursor: 'pointer', background: '#FCFBF5', color: '#054625', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, minHeight: '40px', padding: '0 16px', borderRadius: '999px', whiteSpace: 'nowrap' }}
                    >
                      {showPassword ? 'Masquer' : 'Afficher'}
                    </button>
                  </span>
                </label>

                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer" style={{ fontSize: '14.5px', minHeight: '44px' }}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#054625' }}
                    />
                    Rester connecté·e
                  </label>
                  <Link to="/forgot-password" style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: '#EF4C23' }}>
                    Mot de passe oublié ?
                  </Link>
                </div>

                <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />

                <button
                  type="submit"
                  disabled={loading || (!!process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken)}
                  style={{
                    border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5',
                    fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '58px', borderRadius: '999px', opacity: loading ? 0.7 : 1,
                    transition: 'transform 150ms ease, background-color 150ms ease',
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px) scale(1.01)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                >
                  {loading ? t('auth.connecting') : 'Se connecter'}
                </button>
              </form>

              <div className="flex items-center gap-3.5 animate-fade-in-delay-3">
                <span style={{ flex: 1, height: '1px', background: 'rgba(5,16,4,0.14)' }} />
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.56)' }}>ou</span>
                <span style={{ flex: 1, height: '1px', background: 'rgba(5,16,4,0.14)' }} />
              </div>

              <div className="animate-fade-in-delay-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => console.log('Apple login')}
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', minHeight: '54px', borderRadius: '999px', transition: 'background-color 150ms ease, border-color 150ms ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(114,183,68,0.12)'; e.currentTarget.style.borderColor = '#054625'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(5,16,4,0.16)'; }}
                >
                  Apple
                </button>
                <button
                  type="button"
                  onClick={() => console.log('Google login')}
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', minHeight: '54px', borderRadius: '999px', transition: 'background-color 150ms ease, border-color 150ms ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(114,183,68,0.12)'; e.currentTarget.style.borderColor = '#054625'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(5,16,4,0.16)'; }}
                >
                  Google
                </button>
              </div>

              <span style={{ textAlign: 'center', fontSize: '15px', color: 'rgba(5,16,4,0.74)' }}>
                Pas encore de compte ?{' '}
                <Link to="/signup" style={{ fontWeight: 600 }}>Créer un compte</Link>
              </span>
              <span style={{ textAlign: 'center', fontSize: '13.5px', color: 'rgba(5,16,4,0.55)' }}>
                {t('auth.areYouRestaurant')}{' '}
                <Link to="/business/login" style={{ fontWeight: 600, color: '#054625' }}>{t('auth.restaurantLogin')}</Link>
              </span>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23' }}>Vérification</span>
                <h1 style={{ margin: 0, fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 42px)', lineHeight: 1.05, letterSpacing: '-0.025em' }}>{t('auth.verify2FA')}</h1>
                <p style={{ margin: 0, fontSize: '15px', color: 'rgba(5,16,4,0.7)' }}>
                  {t('auth.codeSentTo')} <strong style={{ color: '#054625' }}>{maskedEmail}</strong>
                </p>
              </div>

              {error && <div className="form-message form-message--error text-center">{error}</div>}

              <form onSubmit={handleOtpSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>
                    {t('auth.verificationCode')}
                  </span>
                  <input
                    type="text"
                    className={inputClass}
                    style={{ textAlign: 'center', letterSpacing: '0.3em', fontSize: '22px', fontWeight: 700 }}
                    placeholder="000000"
                    maxLength={8}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                  />
                </label>

                <button
                  type="submit"
                  disabled={loading || otpCode.length < 6}
                  style={{ border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '58px', borderRadius: '999px', opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? t('auth.verifying') : t('auth.validateCode')}
                </button>
              </form>

              <button
                type="button"
                onClick={() => { setShow2FA(false); setOtpCode(''); setError(''); }}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'rgba(5,16,4,0.6)', fontSize: '14px', textAlign: 'center' }}
              >
                {t('auth.backToLogin')}
              </button>
            </>
          )}
        </div>

        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.58)' }}>
          © 2026 namke · <Link to="/impressum" style={{ color: 'rgba(5,16,4,0.58)' }}>Mentions légales</Link> · <Link to="/privacy-policy" style={{ color: 'rgba(5,16,4,0.58)' }}>Confidentialité</Link>
        </span>
      </div>

      {/* Aside photo + témoignage */}
      <aside style={{ position: 'relative', background: '#FBF9D9', minHeight: '420px', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }} className="hidden lg:flex">
        <img src={namkeAuthBanner} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div className="animate-fade-in-delay-2" style={{ position: 'relative', margin: 'clamp(20px, 2.5vw, 36px)', background: 'rgba(5,70,37,0.92)', color: '#FBF9D9', borderRadius: '34px', padding: 'clamp(22px, 2.6vw, 34px)', display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '460px', backdropFilter: 'blur(6px)' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFC717' }}>Ce soir près de chez vous</span>
          <p style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 'clamp(20px, 2vw, 27px)', lineHeight: 1.35 }}>
            « J'ai retrouvé le riz jollof de ma mère à quinze minutes de chez moi. »
          </p>
          <span className="flex items-center gap-3" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', color: 'rgba(251,249,217,0.8)' }}>
            <span style={{ width: '34px', height: '34px', borderRadius: '999px', background: '#FFC717' }} />
            Awa D. · quatrième commande
          </span>
        </div>
      </aside>
    </div>
  );
}
